import React, { ElementType, useMemo, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';

const ALLOWED_ONCLICK_FUNCTIONS = [
  'backToCalculation',
  'correctInsurantEmail',
  'hideTypoInEmailDomainMessage',
  'mubiReloadPage',
  'scrollToCarHasAdditionalDrivers',
];

export const ONCLICK_CALL_REGEXP = /^(\w+)\((?:'[^']*'(?:, ?'[^']*')*)?\);?$/;

export default function SafeHtml({
  html,
  component = 'span',
  className,
}: {
  html: string;
  component?: ElementType;
  className?: string;
}) {
  const containerRef = useRef<HTMLElement | null>(null);

  const sanitized = useMemo(
    () =>
      DOMPurify.sanitize(html, {
        ALLOWED_ATTR: [
          'href',
          'src',
          'alt',
          'title',
          'width',
          'height',
          'target',
          'rel',
          'class',
          'style',
          'aria-*',
          'role',
          'onclick',
        ],
        ADD_URI_SAFE_ATTR: ['onclick'],
        ALLOW_UNKNOWN_PROTOCOLS: false,
      }),
    [html]
  );

  useEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    container.querySelectorAll<HTMLElement>('[onclick]').forEach((element) => {
      const functionName = element
        .getAttribute('onclick')
        ?.match(ONCLICK_CALL_REGEXP)?.[1];

      if (!functionName || !ALLOWED_ONCLICK_FUNCTIONS.includes(functionName)) {
        element.removeAttribute('onclick');
      }
    });
  }, [sanitized, component]);

  return React.createElement(component, {
    ref: containerRef,
    className,
    dangerouslySetInnerHTML: { __html: sanitized },
  });
}
